import { useCallback, useEffect, useState } from 'react';
import { API } from '../../helpers';

// A page owns its requests; unmount/page changes cancel delivery and transport.
// Failed reads remain failures, never a successful empty list. No auto retry.
export default function useOrganizationListV1116(page, pageSize) {
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(null);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  const key = `${page}:${pageSize}:${revision}`;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const options = {
      signal: controller.signal,
      timeout: 25000,
      organizationReadV1116: true,
    };
    setResult({ key, loading: true, groupsLoading: true });
    const update = (patch) => {
      if (active) setResult((current) => ({ ...current, ...patch }));
    };
    const loadOrganizations = async () => {
      try {
        const response = await API.get(
          `/api/billing/v8/admin/organizations?page=${page}&page_size=${pageSize}`,
          options
        );
        const data = response?.data?.data;
        if (
          !response?.data?.success ||
          !Array.isArray(data?.organizations) ||
          !Number.isInteger(data.total) ||
          data.total < 0 ||
          data.organizations.some((row) => !row?.account?.id || !row?.owner)
        ) {
          throw new Error('Invalid organization list response');
        }
        update({ data, loading: false, error: null });
      } catch (error) {
        update({ loading: false, error });
      }
    };
    const loadGroups = async () => {
      try {
        const response = await API.get('/api/group/', options);
        const groups = response?.data?.data;
        if (
          !response?.data?.success ||
          !Array.isArray(groups) ||
          groups.some((group) => typeof group !== 'string' || !group)
        ) {
          throw new Error('Invalid organization group response');
        }
        update({ groups, groupsLoading: false, groupsError: null });
      } catch (groupsError) {
        update({ groupsLoading: false, groupsError });
      }
    };
    loadOrganizations();
    loadGroups();
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, page, pageSize]);
  const current = result?.key === key ? result : null;
  return {
    organizations: current?.data?.organizations || [],
    total: current?.data?.total,
    loading: !current || current.loading,
    error: current?.error,
    groups: current?.groups || [],
    groupsReady: Boolean(
      current && !current.groupsLoading && !current.groupsError
    ),
    groupsError: current?.groupsError,
    retry,
  };
}
